import React, { useEffect, useState } from 'react'
import ajax from 'ajax'
import PropTypes from 'prop-types'
import PhoneComUser from 'phone-com-user'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import Checkbox, { Variant as CheckboxVariant } from 'checkbox-mui'
import { convertNumberToE164, formatAsYouType } from 'phone-numbers'
import { email, phoneNumber, required, link } from 'validation-rules'
import { Alert, TextField, Typography } from 'foundations-library/components'
import { ConfirmationDialog, CountrySelector, LoaderDialog, PhoneNumberTextField, StateSelector, ZipCodeField } from 'functional-foundations'

import InfoSection from '../../InfoSection'
import { useAjax } from '../../../hooks/useAjax.js'
import { isAmericaState, isCanadianState } from '../../../utils/util'
import useDisableInput from '../../../hooks/useDisableInput'
import useFlatForm from '../../../hooks/useFlatForm'

const useStyles = makeStyles(() => ({
    address: {
    },
    title: {
        marginBottom: '40px'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px'
    },
    twoInputsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px'
    },
    textCenter: {
        textAlign: 'center'
    }
}))

/**
 * @param {object} props
 * @param {object} props.passData Passed data from previous steps
 * @param {Function} props.update Callback function used to complete the step
 * @param {number} props.saveId Step ID, if set the next was clicked
 */
const BusinessAddressAndContact = (props) => {
    const classes = useStyles()

    const phoneNumberFormatHelper = (value) => {
        return formatAsYouType(convertNumberToE164(value).substring(0, 12))
    }

    const { disableAllInputs } = useDisableInput({ mode: props.mode, item: props.editing })

    const bizAddressSavedData = props.passData.business_address
    const businessInfo = props?.passData?.business_info

    const countryCode = businessInfo?.registrationCountry || props.editing.country || 'US'

    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp,
        isFormDataValid
    } = useFlatForm({
        address: bizAddressSavedData ? bizAddressSavedData.address : (props.editing != null ? props.editing.address : null),
        address_2: bizAddressSavedData ? bizAddressSavedData.address_2 : (props.editing != null ? props.editing.address_2 : ''),
        state: bizAddressSavedData?.state || props.editing?.state || null,
        city: bizAddressSavedData ? bizAddressSavedData.city : (props.editing != null ? props.editing.city : null),
        postalCode: bizAddressSavedData ? bizAddressSavedData.postalCode : (props.editing != null ? props.editing.zip : ''),
        primaryBusinessPhone: bizAddressSavedData ? bizAddressSavedData.primaryBusinessPhone : (props.editing != null ? phoneNumberFormatHelper(props.editing.mobile_phone) : ''),
        supportPhone: bizAddressSavedData ? bizAddressSavedData.supportPhone : (props.editing != null ? phoneNumberFormatHelper(props.editing.phone) : ''),
        bizWebsite: bizAddressSavedData ? bizAddressSavedData.bizWebsite : (props.editing != null ? props.editing.company || props.editing.bizWebsite : ''),
        supportEmail: bizAddressSavedData ? bizAddressSavedData.supportEmail : (props.editing != null ? props.editing.email : ''),
        mock: bizAddressSavedData ? bizAddressSavedData.mock : (props.editing != null ? props.editing.is_mock === 'Y' : false)
    }, {
        address_2: true,
        mock: true
    })

    const validateAddress = () => {
        const params = {
            business_address: data.address,
            city: data.city,
            state: data.state,
            postal_code: data.postalCode,
            country_code: countryCode
        }
        const verifyAddressUrl = `${PhoneComUser.getv5ApiRoot()}/addresses/validate-address`
        return ajax.post(verifyAddressUrl, params, 'Bearer')
            .then(rv => {
                if (rv?.status === 200) {
                    return new Promise((resolve, reject) => {
                        if (['CONFIRM', 'ACCEPT'].includes(rv.data.response.result.verdict?.possibleNextAction)) {
                            resolve(rv.data.response)
                            props.update?.({ completed: true, gotoNext: true, saveData: data })
                        } else {
                            setShowInvalidAddrModal(true)
                            reject(new Error('Failed to validate address'))
                        }
                    })
                }
            })
    }
    const { status: validateAddrStatus, execute } = useAjax(validateAddress, false)

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const gotoNextStep = () => {
        setShowConfirmModal(false)
        props.update?.({ completed: true, gotoNext: true, saveData: data })
    }

    const onConfirmHandler = () => {
        setShowConfirmModal(false)
        execute()
    }

    const onRejectHandler = () => {
        setShowConfirmModal(false)
    }

    const [showInvalidAddrModal, setShowInvalidAddrModal] = useState(false)
    const onInvalidAddrConfirmHandler = () => {
        setShowInvalidAddrModal(false)
        gotoNextStep()
    }
    const onInvalidAddrRejectHandler = () => {
        setShowInvalidAddrModal(false)
    }

    const showMockCheckbox = process.env.REACT_APP_10DLC_OPTIONAL_MOCK === 'true'

    useEffect(() => {
        props?.update?.({ disableNextButton: () => !isFormDataValid || validateAddrStatus === 'pending' })
    }, [isFormDataValid, validateAddrStatus])

    // Runs when the next/back button is clicked
    useEffect(() => {
        if (props.saveId) {
            if (props.gotoStepIndex > props.stepIndex) {
                setShowConfirmModal(true)
            } else {
                gotoNextStep()
            }
        }
    }, [props.saveId, props.gotoStepIndex, props.stepIndex])

    return <div data-testid='business-address-and-contact'>
        {
            validateAddrStatus === 'pending'
                ? (
                    <LoaderDialog
                        isOpen
                        title='Validating address...'
                        data-testid='business-address-and-contact-loading-dialog'
                    />
                )
                : (
                    <>
                        <div className={classes.address}>
                            <Typography
                                variant='h5'
                                remoteConfigID='sms_enrollment_address_and_contact'
                                className={classes.title}
                                data-testid='business-address-and-contact-title'
                            />
                            <div className={classes.form}>
                                {validateAddrStatus === 'error' && (
                                    <Alert data-testid='business-address-and-contact-invalid-address'>{getValue('sms_enrollment_invalid_address')}</Alert>
                                )}
                                <div className={classes.twoInputsContainer}>
                                    <TextField
                                        value={data.address}
                                        onChange={setDataProp('address')}
                                        isValid={setIsValidProp('address')}
                                        onClear={onClear('address')}
                                        fullWidth
                                        rules={[required()]}
                                        label={getValue('sms_enrollment_address_line_1')}
                                        data-testid='address'
                                        disabled={disableAllInputs}
                                    />
                                    <TextField
                                        label={getValue('sms_enrollment_address_line_2')}
                                        disabled={disableAllInputs}
                                        fullWidth
                                        value={data.address_2}
                                        onChange={setDataProp('address_2')}
                                        onClear={onClear('address_2')}
                                        data-testid='address_2'
                                    />
                                </div>
                                <div className={classes.twoInputsContainer}>
                                    <TextField
                                        value={data.city}
                                        onChange={setDataProp('city')}
                                        isValid={setIsValidProp('city')}
                                        onClear={onClear('city')}
                                        fullWidth
                                        rules={[required()]}
                                        label={getValue('sms_enrollment_city')}
                                        data-testid='city'
                                        disabled={disableAllInputs}
                                    />
                                    <ZipCodeField
                                        value={data.postalCode}
                                        onChange={setDataProp('postalCode')}
                                        isValid={setIsValidProp('postalCode')}
                                        onClear={onClear('postalCode')}
                                        validate
                                        required
                                        countryCode={countryCode}
                                        fullWidth
                                        withLabel
                                        data-testid='postalCode'
                                        disabled={disableAllInputs}
                                    />
                                </div>
                                <div className={classes.twoInputsContainer}>
                                    <CountrySelector
                                        value={countryCode}
                                        disabled
                                        fullWidth
                                        label={getValue('sms_enrollment_country')}
                                        rules={[required()]}
                                        data-testid='registrationCountry'
                                    />
                                    <StateSelector
                                        value={data.state}
                                        onChange={setDataProp('state')}
                                        isValid={setIsValidProp('state')}
                                        onClear={onClear('state')}
                                        fullWidth
                                        label={getValue('sms_enrollment_state')}
                                        rules={[required()]}
                                        countryCode={countryCode}
                                        data-testid='state'
                                        disabled={disableAllInputs}
                                    />
                                </div>
                                <TextField
                                    type='email'
                                    value={data.supportEmail}
                                    onChange={setDataProp('supportEmail')}
                                    isValid={setIsValidProp('supportEmail')}
                                    onClear={onClear('supportEmail')}
                                    label={getValue('sms_enrollment_support_email')}
                                    rules={[required(), email()]}
                                    helperText={getValue('sms_enrollment_support_email_helper_text')}
                                    data-testid='supportEmail'
                                    disabled={disableAllInputs}
                                />
                                <div className={classes.twoInputsContainer}>
                                    <PhoneNumberTextField
                                        value={data.primaryBusinessPhone}
                                        onFormatChange={setDataProp('primaryBusinessPhone')}
                                        isValid={setIsValidProp('primaryBusinessPhone')}
                                        onClear={onClear('primaryBusinessPhone')}
                                        fullWidth
                                        rules={[required(), phoneNumber()]}
                                        helperText='Must be a valid phone number'
                                        label={getValue('sms_enrollment_primary_number')}
                                        data-testid='primaryBusinessPhone'
                                        disabled={disableAllInputs}
                                    />
                                    <PhoneNumberTextField
                                        value={data.supportPhone}
                                        onFormatChange={setDataProp('supportPhone')}
                                        isValid={setIsValidProp('supportPhone')}
                                        onClear={onClear('supportPhone')}
                                        fullWidth
                                        rules={[required(), phoneNumber()]}
                                        helperText='Must be a valid phone number, will appear in SMS messages to customers'
                                        label={getValue('sms_enrollment_support_number')}
                                        data-testid='supportPhone'
                                        disabled={disableAllInputs}
                                    />
                                </div>

                                <TextField
                                    type='url'
                                    value={data.bizWebsite}
                                    onChange={setDataProp('bizWebsite')}
                                    isValid={setIsValidProp('bizWebsite')}
                                    onClear={onClear('bizWebsite')}
                                    rules={[required(), link()]}
                                    label={getValue('sms_enrollment_business_website')}
                                    helperText={getValue('sms_enrollment_business_website_helper_text')}
                                    data-testid='bizWebsite'
                                    disabled={disableAllInputs}
                                />
                                <InfoSection data-testid='info-section'>
                                    {getValue('sms_enrollment_privacy_policy_information_title')}
                                    <br />
                                    <br />
                                    <ul>
                                        {JSON.parse(getValue('sms_enrollment_privacy_policy_information_reasons')).map((x, i) => <li key={`sms-enr-reasons-${i}`}>{x}</li>)}
                                    </ul>
                                </InfoSection>
                                {showMockCheckbox &&
                                    <div>
                                        <Checkbox
                                            variant={CheckboxVariant.PRIMARY}
                                            checked={data.mock}
                                            onChange={(type, checked) => setDataProp('mock')(!data.mock)}
                                            label='Mock brand'
                                            data-testid='mock-checkbox'
                                            disabled={disableAllInputs}
                                        />
                                    </div>
                                }
                            </div>
                        </div>
                    </>
                )
        }
        <ConfirmationDialog
            onConfirm={onConfirmHandler}
            isOpen={showConfirmModal}
            cancelText='Edit'
            confirmText='Confirm and continue'
            onCancel={onRejectHandler}
            onClose={onRejectHandler}
            title='Confirmation'
            titleAlign='center'
            width={600}
            cancelProps={{ variant: 'outlined' }}
            className={classes.textCenter}
        >
            {getValue('sms_enrollment_please_confirm_business_registration')}
        </ConfirmationDialog>
        <ConfirmationDialog
            title='Confirmation'
            titleAlign='center'
            isOpen={showInvalidAddrModal}
            onConfirm={onInvalidAddrConfirmHandler}
            onCancel={onInvalidAddrRejectHandler}
            onClose={onInvalidAddrRejectHandler}
            confirmText='Proceed'
        >
            {getValue('sms_enrollment_invalid_business_addr')}
        </ConfirmationDialog>
    </div>
}

BusinessAddressAndContact.propTypes = {
    passData: PropTypes.object,
    update: PropTypes.func,
    saveId: PropTypes.number,
    gotoStepIndex: PropTypes.number,
    stepIndex: PropTypes.number,
    editing: PropTypes.object,
    showContainer: PropTypes.bool,
    mode: PropTypes.string
}

export default BusinessAddressAndContact
